/**
 * DraftParentBanner - Read-only banner shown in place of the reverse
 * association field when CrmCreateForm is nested inside a draft-parent
 * create flow (e.g. creating a deal from within a company's create form).
 */

import { Link01 } from '@untitledui/icons';

import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { LabelWithIndicator } from '@/lib/crm/components/fields/LabelWithIndicator';

export interface DraftParentBannerProps {
  /** Label for the reverse association field this banner replaces. */
  label: string;
  /** Lowercased entity type label to use when displayName is unavailable. */
  parentTypeLabel: string;
  /** Display name of the parent draft, when known. */
  displayName?: string;
}

export function DraftParentBanner({
  label,
  parentTypeLabel,
  displayName,
}: DraftParentBannerProps) {
  const bannerText = displayName
    ? `Will be linked to ${displayName} when you create it`
    : `Will be linked to the ${parentTypeLabel} you're creating`;

  return (
    <div className='md:col-span-2 flex flex-col gap-1.5'>
      <LabelWithIndicator label={label} />
      <div
        data-testid='draft-parent-link-banner'
        className='flex items-center gap-3 rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)] p-3'
      >
        <span aria-hidden='true'>
          <FeaturedIcon
            icon={Link01}
            color='brand'
            theme='light-outline'
            size='sm'
          />
        </span>
        <span className='text-sm text-[var(--color-text-secondary)]'>
          {bannerText}
        </span>
      </div>
    </div>
  );
}
